// src/components/EnterpriseNewBusinessUnitModal.js
import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useApi } from "../hooks/useApi";

const EnterpriseNewBusinessUnitModal = ({ isOpen, onClose, onBusinessUnitAdded, existingBUIds = [] }) => {
    const [businessUnits, setBusinessUnits] = useState([]);
    const [selectedBUId, setSelectedBUId] = useState("");
    const [loadingBUs, setLoadingBUs] = useState(true);

    const apiCall = useApi();

    // Fetch all business units when modal opens
    useEffect(() => {
        if (isOpen) fetchBusinessUnits();
    }, [isOpen]);

    const fetchBusinessUnits = async () => {
        try {
            const res = await apiCall("GET", `/businessUnits/all`);
            // Filter out BUs that are already added for this product category
            const availableBUs = (res || []).filter(bu => !existingBUIds.includes(bu.businessUnitId));
            setBusinessUnits(availableBUs);
        } catch (err) {} finally {
            setLoadingBUs(false);
        }
    };

    // Handle save (BU selection confirmation)
    const handleSave = async (e) => {
        e.preventDefault();

        if (!selectedBUId) {
            alert("Please select a business unit first.");
            return;
        }

        const selectedBU = businessUnits.find((bu) => bu.businessUnitId.toString() === selectedBUId);

        try {
            // Update BusinessUnitCategory flag in backend
            await apiCall("PUT", `/businessUnits/businessUnitUpdate-category/${selectedBUId}`, { businessUnitCategory: "Enterprise" });

            // Trigger parent callback and close modal
            onBusinessUnitAdded?.(selectedBU);
            onClose();
        } catch (error) {
            alert("Failed to add business unit. Check console for details.");
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-6 border border-border w-full max-w-lg shadow-lg rounded-md bg-theme-secondary">

                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-content-primary">Add Business Unit to Enterprise</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <form onSubmit={handleSave} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Business Unit</label>
                        <select
                            value={selectedBUId}
                            onChange={(e) => setSelectedBUId(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                            disabled={loadingBUs}
                        >
                            <option value="">-- Select a Business Unit --</option>
                            {businessUnits.map((bu) => (
                                <option
                                    key={bu.businessUnitId}
                                    value={bu.businessUnitId}
                                    title={bu.businessUnitName}
                                >
                                    {bu.businessUnitName}
                                </option>
                            ))}
                        </select>
                        {!loadingBUs && businessUnits.length === 0 && (
                            <p className="text-sm text-gray-500 mt-2">
                                No available business units. Create business units from the Business Units management page.
                            </p>
                        )}
                    </div>

                    {/* Buttons */}
                    <div className="flex justify-end space-x-3 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={!selectedBUId}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Save
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EnterpriseNewBusinessUnitModal;
